import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface NotificationData {
  case_id?: string;
  entity_id?: string;
  action?: string;
  previous_status?: string;
  current_status?: string;
  [key: string]: unknown;
}

export interface Notification {
  id: string;
  title: string;
  body: string;
  data: NotificationData;
  timestamp: number;
  read: boolean;
}

interface NotificationsState {
  notifications: Notification[];
  maxStored: number;
}

const MAX_STORED = 100;

const initialState: NotificationsState = {
  notifications: [],
  maxStored: MAX_STORED,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Omit<Notification, "read">>) => {
      const notification: Notification = {
        ...action.payload,
        read: false,
      };
      state.notifications.unshift(notification);
      if (state.notifications.length > state.maxStored) {
        state.notifications = state.notifications.slice(0, state.maxStored);
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const item = state.notifications.find((n) => n.id === action.payload);
      if (item) item.read = true;
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((n) => (n.read = true));
    },
    deleteNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearAll: (state) => {
      state.notifications = [];
    },
  },
});

export const {
  addNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAll,
} = notificationsSlice.actions;

export const selectNotifications = (state: { notifications: NotificationsState }) =>
  state.notifications.notifications;
export const selectUnreadCount = (state: { notifications: NotificationsState }) =>
  state.notifications.notifications.filter((n) => !n.read).length;

export default notificationsSlice.reducer;
